'use client'

import React, { useCallback, useRef, useState } from 'react'
import classNames from 'classnames'
import CollectionIcon from '@/components/collection-icon'
import type { BidCollection } from '@/utils/bids'

const isBroken = (img: HTMLImageElement) => img.complete && img.naturalWidth === 0

/**
 * Watches a subtree for images that fail to load — including ones that failed
 * before hydration, when React's onError is not attached yet. `error` does
 * not bubble, so it is caught in the capture phase.
 */
export function useBrokenImageWatch() {
	const [broken, setBroken] = useState(false)
	const cleanup = useRef<(() => void) | null>(null)
	const ref = useCallback((el: HTMLElement | null) => {
		cleanup.current?.()
		cleanup.current = null
		if (!el) return
		const onError = (e: Event) => {
			if (e.target instanceof HTMLImageElement) setBroken(true)
		}
		el.addEventListener('error', onError, true)
		cleanup.current = () => el.removeEventListener('error', onError, true)
		if ([...el.querySelectorAll('img')].some(isBroken)) setBroken(true)
	}, [])
	return [ref, broken] as const
}

/** Two-letter monogram on a quiet surface: never a broken-image glyph. */
export function Monogram({ name, className }: { name?: string | null; className?: string }) {
	const letters = (name || '?')
		.replace(/[^\p{L}\p{N} ]/gu, '')
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((w) => w[0])
		.join('')
		.toUpperCase()
	return (
		<span
			aria-hidden="true"
			className={classNames(
				'absolute inset-0 flex items-center justify-center bg-muted text-[11px] font-semibold leading-none text-muted-foreground',
				className
			)}
		>
			{letters || '?'}
		</span>
	)
}

/** Collection artwork filling its (relative, overflow-hidden) parent. */
export default function CollectionArt({
	collection,
	imageClassName
}: {
	collection: BidCollection
	imageClassName?: string
}) {
	const [ref, broken] = useBrokenImageWatch()
	if (broken) return <Monogram name={collection.name} />
	return (
		<span ref={ref} className="absolute inset-0 block">
			<CollectionIcon
				collection={collection as any}
				className="absolute inset-0 !size-full !rounded-none"
				imageClassName={classNames('!size-full object-cover', imageClassName)}
			/>
		</span>
	)
}
